import axios from 'axios';
import { getApiBaseUrl } from './api';
import type { Validation, Ticket, ActivityEvent, WorkspaceContext, PlannerEvent } from '../types/agents';

// getApiBaseUrl() returns "http://localhost:8000/api" in dev
// Backend route prefix is "/api/agents", so we need "/agents" suffix
const getBase = () => `${getApiBaseUrl()}/agents`;

export const agentsApi = {
  // ── Validations ─────────────────────────────────────────────────
  listValidations: (params?: { status?: string; limit?: number }) =>
    axios.get<{ content: { text: string }[] }>(`${getBase()}/validations`, { params }),

  getValidation: (id: string) =>
    axios.get(`${getBase()}/validations/${id}`),

  getWorkspaceContext: (id: string) =>
    axios.get(`${getBase()}/validations/${id}/context`),

  getAppStatus: (id: string) =>
    axios.get(`${getBase()}/validations/${id}/app-status`),

  getActivityTimeline: (id: string, params?: { since?: string; ticketId?: string; limit?: number }) =>
    axios.get(`${getBase()}/validations/${id}/activity`, { params }),

  readFile: (validationId: string, filePath: string, params?: { startLine?: number; endLine?: number }) =>
    axios.get(`${getBase()}/validations/${validationId}/files/${filePath}`, { params }),

  searchCode: (validationId: string, query: string, glob?: string) =>
    axios.get(`${getBase()}/validations/${validationId}/search`, { params: { q: query, glob } }),

  // ── Tickets ─────────────────────────────────────────────────────
  listTickets: (validationId: string, status?: string) =>
    axios.get(`${getBase()}/validations/${validationId}/tickets`, { params: status ? { status } : undefined }),

  getTicket: (ticketId: string) =>
    axios.get(`${getBase()}/tickets/${ticketId}`),

  createTicket: (data: {
    validationId: string;
    title: string;
    description: string;
    type: string;
    priority?: string;
    plan?: string;
    affectedFiles?: string[];
    acceptanceCriteria?: string;
    requiresHumanApproval?: boolean;
  }) => axios.post(`${getBase()}/tickets`, data),

  submitFeedback: (ticketId: string, feedback: string, approve: boolean = true) =>
    axios.post(`${getBase()}/tickets/${ticketId}/feedback`, { feedback, approve }),

  triggerExecution: (ticketId: string) =>
    axios.post(`${getBase()}/tickets/${ticketId}/execute`),
};

/**
 * SSE planner stream. Posts to the planner endpoint and reads SSE events.
 * Returns an abort function.
 */
export function streamPlanner(
  validationId: string | null,
  message: string,
  history: { role: string; content: string }[],
  onEvent: (event: PlannerEvent) => void,
  onError: (error: string) => void,
  onComplete: () => void,
): () => void {
  const controller = new AbortController();
  const { signal } = controller;

  const apiUrl = `${getApiBaseUrl()}/agents/planner/stream`;
  const token = localStorage.getItem('auth_token');

  fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      ...(validationId ? { validation_id: validationId } : {}),
      message,
      history,
    }),
    signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        const text = await response.text();
        onError(`HTTP ${response.status}: ${text}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('Could not get response reader');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            // event type line — skip, we use data.type instead
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6)) as PlannerEvent;
              if (data.type === 'complete') {
                completed = true;
                onComplete();
              } else if (data.type === 'error') {
                completed = true;
                onError(data.message || 'Unknown error');
              } else {
                onEvent(data);
              }
            } catch {
              // Ignore malformed JSON lines
            }
          }
        }
      }

      // Only call onComplete if we didn't already get a complete/error event
      if (!completed) {
        onComplete();
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        onError(err.message || 'Stream failed');
      }
    });

  return () => controller.abort();
}
